import { useCallback, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useMe } from "@/hooks/useAuth";
import { useTenantLayout } from "@/hooks/useTenantLayout";
import { useFilteredAdminNav } from "@/hooks/useEnabledNav";
import { api, unwrap } from "@/lib/api";
import { AppShell, ShellFooterCard, ShellSkeleton, orderBySection, type ShellNavItem } from "./AppShell";

const ADMIN_SECTIONS: Record<string, string> = {
  Dashboard: "Overview",
  "Manage Client": "Clients",
  "Partner Management": "Clients",
  "KYC Management": "Clients",
  "Manage Accounts": "Clients",
  "Manage Request": "Money",
  Finance: "Money",
  "Fund Transfer": "Money",
  "Manage Banks": "Money",
  Offers: "Money",
  "Manage Trade": "Trading",
  "Group Settings": "Trading",
  Reports: "Insights",
  "Admin Profile": "Administration",
  "Manage Admin": "Administration",
  "Manager Settings": "Administration",
  "Support Tickets": "Administration",
  Settings: "Administration",
};
const ORDER = ["Overview", "Clients", "Money", "Trading", "Insights", "Administration"];
const sectionOf = (i: ShellNavItem) => ADMIN_SECTIONS[i.label] ?? "More";

/**
 * Broker admin console. The tenant's layout preset still matters: the
 * "icon-rail" preset opens with the sidebar collapsed to icons.
 */
export function AdminShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const { preset } = useTenantLayout(me ?? null);
  const filteredNav = useFilteredAdminNav();
  const qc = useQueryClient();

  const nav = useMemo(() => orderBySection(filteredNav as ShellNavItem[], sectionOf, ORDER), [filteredNav]);
  const prefetch = useCallback((to: string) => {
    if (to === "/admin/dashboard") {
      qc.prefetchQuery({
        queryKey: ["dashboard-summary"],
        queryFn: async () => unwrap((await api.get("/admin/dashboard/summary")).data),
        staleTime: 30_000,
      });
    }
  }, [qc]);

  if (isPending) return <ShellSkeleton items={14} />;
  if (isError || !me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  // [workstream E2] A manager granted an add-on admin group gets the admin
  // console too (backend rbac limits them to the granted sections).
  const managerAddon = me.role === "manager" && (me.fullAccess || (me.permissions ?? []).some((p: string) => !p.startsWith("/admin/impersonate")));
  if (me.role !== "admin" && !managerAddon) return <Navigate to={me.role === "manager" ? "/manager/dashboard" : "/login"} replace />;

  return (
    <AppShell
      nav={nav}
      sectionOf={sectionOf}
      storageKey="admin"
      defaultCollapsed={preset === "icon-rail"}
      prefetch={prefetch}
      footer={<ShellFooterCard icon={ShieldCheck} title="Broker Console" subtitle={me.fullName ?? me.email} />}
    />
  );
}
